import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { ShieldCheck, Loader2, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
function googleLogin() {
  const redirectUrl = window.location.origin + "/auth/callback";
  window.location.href =
    "https://auth.emergentagent.com/?redirect=" + encodeURIComponent(redirectUrl);
}

export default function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const { setUser } = useAuth();
  const [mode, setMode] = useState("password"); // password | magic
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const from = location.state?.from || "/dashboard";

  const onSubmit = async (e) => {
    e.preventDefault();
    if (loading) return;
    setLoading(true);
    try {
      if (mode === "password") {
        const { data } = await api.post("/auth/login", { email, password });
        setUser(data);
        navigate(from, { replace: true });
      } else {
        const { data } = await api.post("/auth/magic-link/request", { email });
        if (data?.magic_url) {
          toast.success("Magic link issued (demo)", {
            description: "No email provider is configured — open the link to continue.",
            action: {
              label: "Open link",
              onClick: () => (window.location.href = data.magic_url),
            },
            duration: 12000,
          });
        } else {
          toast.success("Check your email", {
            description: "We sent a one-time sign-in link.",
          });
        }
      }
    } catch (err) {
      toast.error("Sign in failed", { description: formatApiError(err) });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-white">
      <div className="hidden lg:flex relative bg-slate-900 text-white p-12 flex-col justify-between overflow-hidden">
        <div className="absolute -top-24 -right-24 h-72 w-72 rounded-full bg-blue-500/30 blur-3xl" />
        <div className="absolute -bottom-32 -left-12 h-72 w-72 rounded-full bg-cyan-400/20 blur-3xl" />
        <Link to="/" className="relative flex items-center gap-2">
          <span className="h-9 w-9 rounded-md bg-blue-600 inline-flex items-center justify-center">
            <ShieldCheck className="h-5 w-5" strokeWidth={2.5} />
          </span>
          <span className="font-display font-semibold text-xl">AgentFlow<span className="text-blue-400">.</span></span>
        </Link>
        <div className="relative max-w-md">
          <h1 className="font-display text-4xl font-semibold leading-tight">
            Your AI compliance{" "}
            <span className="text-cyan-300">team in a tab.</span>
          </h1>
          <p className="mt-5 text-slate-300 leading-relaxed">
            Welcome back. Sign in to continue monitoring your regulatory exposure.
          </p>
        </div>
        <p className="relative text-xs text-slate-400">
          SOC 2 Type II · GDPR · ISO 27001 ready
        </p>
      </div>

      <div className="flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-sm">
          <Link to="/" className="lg:hidden flex items-center gap-2 mb-8">
            <span className="h-8 w-8 rounded-md bg-blue-600 inline-flex items-center justify-center text-white">
              <ShieldCheck className="h-4 w-4" />
            </span>
            <span className="font-display font-semibold text-lg">AgentFlow<span className="text-blue-600">.</span></span>
          </Link>
          <h2 className="font-display text-2xl font-semibold tracking-tight text-slate-900">
            Sign in to AgentFlow
          </h2>
          <p className="mt-1.5 text-sm text-slate-600">
            New here?{" "}
            <Link to="/register" className="text-blue-600 hover:text-blue-700 font-medium">
              Create an account
            </Link>
          </p>

          <button
            type="button"
            onClick={googleLogin}
            data-testid="login-google-btn"
            className="mt-7 w-full inline-flex items-center justify-center gap-2 rounded-lg border border-slate-300 bg-white px-4 py-2.5 text-sm font-medium text-slate-900 shadow-sm hover:bg-slate-50 transition-colors"
          >
            <GoogleIcon /> Continue with Google
          </button>

          <div className="my-6 flex items-center gap-3 text-xs uppercase tracking-wider text-slate-400">
            <span className="flex-1 h-px bg-slate-200" /> or <span className="flex-1 h-px bg-slate-200" />
          </div>

          <div className="flex gap-2 mb-4">
            <ToggleBtn active={mode === "password"} onClick={() => setMode("password")} testId="login-mode-password">
              Password
            </ToggleBtn>
            <ToggleBtn active={mode === "magic"} onClick={() => setMode("magic")} testId="login-mode-magic">
              Magic link
            </ToggleBtn>
          </div>

          <form onSubmit={onSubmit} className="space-y-3" data-testid="login-form">
            <div>
              <Label htmlFor="email">Work email</Label>
              <Input
                id="email" type="email" required value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@company.com"
                className="mt-1 h-11"
                data-testid="login-email"
              />
            </div>
            {mode === "password" && (
              <div>
                <Label htmlFor="password">Password</Label>
                <Input
                  id="password" type="password" required value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="mt-1 h-11"
                  data-testid="login-password"
                />
              </div>
            )}
            <Button
              type="submit"
              disabled={loading}
              className="w-full h-11 rounded-lg bg-blue-600 hover:bg-blue-700 text-white"
              data-testid="login-submit"
            >
              {loading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : mode === "password" ? (
                <>Sign in <ArrowRight className="ml-2 h-4 w-4" /></>
              ) : (
                "Send magic link"
              )}
            </Button>
          </form>

          <p className="mt-6 text-xs text-slate-500 leading-relaxed">
            By signing in you agree to our terms and privacy policy. Your data is encrypted in transit and at rest.
          </p>
        </div>
      </div>
    </div>
  );
}

function ToggleBtn({ active, onClick, children, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className={
        "flex-1 rounded-lg border px-3 py-2 text-sm font-medium transition-colors " +
        (active
          ? "border-blue-600 bg-blue-50 text-blue-700"
          : "border-slate-200 bg-white text-slate-600 hover:bg-slate-50")
      }
    >
      {children}
    </button>
  );
}

function GoogleIcon() {
  return (
    <svg viewBox="0 0 24 24" className="h-4 w-4" aria-hidden="true">
      <path fill="#EA4335" d="M12 11v3.27h7.43c-.3 1.65-2.12 4.83-7.43 4.83-4.47 0-8.12-3.7-8.12-8.27S7.53 2.56 12 2.56c2.54 0 4.24 1.08 5.21 2.02L19.6 2.2C18.04.78 15.27-.44 12-.44 5.37-.44 0 4.97 0 11.83S5.37 24.1 12 24.1c6.93 0 11.5-4.87 11.5-11.73 0-.79-.08-1.39-.18-2H12z"/>
    </svg>
  );
}
